import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';

/**
 * Two-step email-confirmation dialog for destructive admin actions.
 *
 *   1. Mounting fires {@link requestCode} → backend emails a 6-digit code
 *      to the SUPER_ADMIN's own address.
 *   2. The admin types the code and clicks confirm → {@link confirm} is
 *      called with the code and the actual deletion happens server-side.
 *
 * Sharing this between user-delete and provider-delete keeps the UX
 * (and resend cooldown / wording) identical across both entry points.
 */
export function ConfirmDeleteByEmailDialog({
  open,
  onClose,
  onSuccess,
  title,
  targetLabel,
  requestCode,
  confirm,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  title: string;
  /** Human-friendly description of what gets deleted, e.g. "Studio Tresses". */
  targetLabel: string;
  requestCode: () => Promise<void>;
  confirm: (code: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const [code, setCode] = useState('');
  const [codeRequested, setCodeRequested] = useState(false);

  const request = useMutation({
    mutationFn: requestCode,
    onSuccess: () => {
      setCodeRequested(true);
      notify.info(
        t('admin.deleteCodeSent', {
          defaultValue:
            'Un code de confirmation a été envoyé à votre adresse email.',
        }),
      );
    },
    onError: (err) => {
      notify.error(translate(err));
    },
  });

  const submit = useMutation({
    mutationFn: () => confirm(code.trim()),
    onSuccess: () => {
      onSuccess();
      handleClose();
    },
    onError: (err) => notify.error(translate(err)),
  });

  // Fire the request automatically when the dialog opens.
  useEffect(() => {
    if (open && !codeRequested && !request.isPending) {
      request.mutate();
    }
    if (!open) {
      // Reset state when the dialog is closed so a subsequent open starts fresh.
      setCode('');
      setCodeRequested(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (submit.isPending) return;
    onClose();
  };

  const canSubmit =
    /^\d{6}$/.test(code.trim()) && !submit.isPending && codeRequested;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert
            severity="warning"
            icon={<MailOutlineRoundedIcon fontSize="inherit" />}
          >
            {t('admin.deleteEmailGate', {
              defaultValue:
                'Pour confirmer cette action, saisissez le code à 6 chiffres envoyé à votre adresse email.',
            })}
          </Alert>

          <Typography variant="body2" color="text.secondary">
            {t('admin.deleteTargetLabel', {
              defaultValue: 'Action sur :',
            })}{' '}
            <Box component="strong" sx={{ color: 'text.primary' }}>
              {targetLabel}
            </Box>
          </Typography>

          <TextField
            label={t('admin.confirmCode', { defaultValue: 'Code à 6 chiffres' })}
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            placeholder="000000"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]{6}',
              maxLength: 6,
              autoComplete: 'one-time-code',
            }}
            disabled={!codeRequested || submit.isPending}
            autoFocus
            fullWidth
          />

          <Box>
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={() => request.mutate()}
              disabled={request.isPending || submit.isPending}
              sx={{ fontSize: '0.875rem' }}
            >
              {request.isPending
                ? t('common.loading')
                : codeRequested
                  ? t('admin.deleteResendCode', {
                      defaultValue: 'Renvoyer le code',
                    })
                  : t('admin.deleteSendCode', {
                      defaultValue: 'Envoyer le code',
                    })}
            </Link>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submit.isPending}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!canSubmit}
          onClick={() => submit.mutate()}
        >
          {submit.isPending
            ? t('common.loading')
            : t('admin.deleteConfirm', {
                defaultValue: 'Supprimer définitivement',
              })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
